import ExcelJS from "exceljs";
import type { ReportTableColumn } from "../components/ReportPointsTable";
import { documentoDesdeTabla } from "../documento/desdeTabla";
import { descargarPdf } from "../documento/pdf";
import { brand } from "@/config/theme";
import { hexToArgb } from "@/theme/preset";

const BRAND_ARGB = hexToArgb(brand.primary);

interface ExportMeta {
  title: string;
  period: string;
  fileName: string;
}

const triggerDownload = (blob: Blob, fileName: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
};

// Para volúmenes grandes (80k+) procesamos en chunks para no congelar el hilo
// principal. Cada chunk escribe N filas y cede un tick con setTimeout.
const EXCEL_CHUNK_SIZE = 5000;

export const exportHistoricToExcel = async <T extends { id: string }>(
  rows: T[],
  columns: ReportTableColumn<T>[],
  meta: ExportMeta,
): Promise<void> => {
  const workbook = new ExcelJS.Workbook();
  const ws = workbook.addWorksheet("Recorrido", {
    views: [{ showGridLines: false }],
  });

  const totalCols = columns.length;

  ws.mergeCells(1, 1, 1, totalCols);
  const titleCell = ws.getCell(1, 1);
  titleCell.value = meta.title;
  titleCell.font = { bold: true, size: 16, color: { argb: BRAND_ARGB } };
  titleCell.alignment = { horizontal: "center" };

  ws.mergeCells(2, 1, 2, totalCols);
  const periodCell = ws.getCell(2, 1);
  periodCell.value = meta.period;
  periodCell.font = { italic: true, size: 10, color: { argb: "FF6B7280" } };
  periodCell.alignment = { horizontal: "center" };

  const headerRowNum = 4;
  const headerRow = ws.getRow(headerRowNum);
  columns.forEach((col, i) => {
    const cell = headerRow.getCell(i + 1);
    cell.value = col.header;
    cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: BRAND_ARGB },
    };
    cell.alignment = { horizontal: "center" };
    ws.getColumn(i + 1).width = 18;
  });

  for (let start = 0; start < rows.length; start += EXCEL_CHUNK_SIZE) {
    const end = Math.min(start + EXCEL_CHUNK_SIZE, rows.length);
    for (let rowIndex = start; rowIndex < end; rowIndex++) {
      const row = rows[rowIndex];
      const r = ws.getRow(headerRowNum + 1 + rowIndex);
      columns.forEach((col, i) => {
        r.getCell(i + 1).value = (row[col.key] ?? "") as ExcelJS.CellValue;
      });
    }
    if (end < rows.length) {
      await new Promise((r) => setTimeout(r, 0));
    }
  }

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  triggerDownload(blob, meta.fileName);
};

/**
 * El PDF de una tabla de reportes.
 *
 * ── Qué cambió ──────────────────────────────────────────────────────────────
 * Era un `autoTable` con el título encima: sin identidad, sin resumen y sin
 * manera de saber, una vez impreso, de qué unidad y de qué periodo era. Ahora
 * arma el mismo documento que previsualiza la pantalla y lo imprime con la
 * hoja de WAYAK.
 *
 * La firma no se tocó a propósito: siete secciones la llaman directo desde su
 * propio botón de descarga y todas mejoran sin cambiar una línea. Las que
 * quieran un resumen propio pasan `bloques`.
 */
export const exportHistoricToPdf = <T extends { id: string }>(
  rows: T[],
  columns: ReportTableColumn<T>[],
  meta: ExportMeta,
  bloques: Parameters<typeof documentoDesdeTabla>[3] = [],
): void => {
  // `fileName` viene con extensión y `descargarPdf` la pone: sin quitarla
  // salían archivos `recorrido.pdf.pdf`.
  const archivo = meta.fileName.replace(/\.pdf$/i, "");
  // `descargarPdf` es asincrona desde que el documento puede traer retratos.
  // Estas siete secciones la llaman sin esperar, asi que el fallo se reporta
  // aqui: una promesa suelta que revienta no deja rastro en ningun lado.
  void descargarPdf(
    documentoDesdeTabla(rows, columns, {
      titulo: meta.title,
      periodo: meta.period,
      archivo,
    }, bloques),
    new Date().toLocaleString("es-MX", { dateStyle: "short", timeStyle: "short" }),
  ).catch((err) => {
    console.error("[exportHistoricToPdf] no se pudo generar el PDF", err);
  });
};
